<template>
    <v-container class="mt-10" max-width="1200">
        <!-- Header -->
        <div class="profile-header mb-8">
            <div class="d-flex align-center mb-2">
                <v-icon color="primary" large class="mr-3">mdi-account</v-icon>
                <h1 class="font-weight-bold">My Profile</h1>
            </div>
            <v-btn color="primary" outlined @click="openEditProfile" class="ml-4">
                <v-icon left>mdi-pencil</v-icon>
                Edit Profile
            </v-btn>
            <v-btn outlined @click="showChangePassword = true" class="ml-2">
                <v-icon left>mdi-lock-reset</v-icon>
                Change Password
            </v-btn>
        </div>
        <div>
            <v-card elevation="1" class="mb-6">
                <v-card-text class="pa-6">
                    <v-row>
                        <!-- Profile Picture and Basic Info -->
                        <v-col cols="12" md="4" class="text-center">
                            <v-avatar color="primary" size="120" class="mb-4">
                                <v-img v-if="user.profile_picture"
                                    :src="getProfilePictureUrl(user.profile_picture)"></v-img>
                                <span v-else class="white--text text-h4">
                                    {{ getUserInitials(user) }}
                                </span>
                            </v-avatar>

                            <h2 class="text-h5 font-weight-bold mb-2">
                                {{ getUserFullName(user) }}
                            </h2>
                            <v-chip color="primary" outlined class="mb-2">
                                {{ user?.role?.name || "No Role" }}
                            </v-chip>

                            <div class="text-body-2 text--secondary">
                                <div class="mb-1">
                                    <v-icon small class="mr-2">mdi-calendar</v-icon>
                                    Joined {{ formatDate(user.created_at) }}
                                </div>
                            </div>
                        </v-col>

                        <!-- Contact Information -->
                        <v-col cols="12" md="8">
                            <h3 class="text-h6 font-weight-bold mb-4">Contact Information</h3>

                            <v-row>
                                <v-col cols="12" sm="6">
                                    <div class="info-item mb-4">
                                        <div class="d-flex align-center">
                                            <v-icon color="primary" class="mr-3">mdi-email</v-icon>
                                            <div>
                                                <div class="text-caption text--secondary">Email</div>
                                                <div class="text-body-1">
                                                    {{ user.email || "Not provided" }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div class="info-item mb-4">
                                        <div class="d-flex align-center">
                                            <v-icon color="primary" class="mr-3">mdi-phone</v-icon>
                                            <div>
                                                <div class="text-caption text--secondary">Phone</div>
                                                <div class="text-body-1">
                                                    {{ user.phone || "Not provided" }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div class="info-item mb-4">
                                        <div class="d-flex align-center">
                                            <v-icon color="primary" class="mr-3">mdi-briefcase</v-icon>
                                            <div>
                                                <div class="text-caption text--secondary">Position</div>
                                                <div class="text-body-1">
                                                    {{ user.position_name || "Not provided" }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div class="info-item mb-4">
                                        <div class="d-flex align-center">
                                            <v-icon color="primary" class="mr-3">mdi-map-marker</v-icon>
                                            <div>
                                                <div class="text-caption text--secondary">Location</div>
                                                <div class="text-body-1">
                                                    {{ user.location || "Not provided" }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" sm="6">
                                    <div class="info-item mb-4">
                                        <div class="d-flex align-center">
                                            <v-icon color="primary" class="mr-3">mdi-calendar-check</v-icon>
                                            <div>
                                                <div class="text-caption text--secondary">Joined Date</div>
                                                <div class="text-body-1">
                                                    {{ formatDate(user.joined_date) || "Not provided" }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>

                    <!-- Skills Section -->
                    <v-divider class="my-6"></v-divider>

                    <div class="mb-6">
                        <h3 class="text-h6 font-weight-bold mb-4">
                            <v-icon color="primary" class="mr-2">mdi-star</v-icon>
                            Skills & Expertise
                        </h3>

                        <div v-if="user.skills && user.skills.length > 0">
                            <v-chip v-for="(skill, index) in user.skills" :key="index" class="mr-2 mb-2" color="blue"
                                outlined small>
                                {{ skill }}
                            </v-chip>
                        </div>
                        <div v-else class="text-body-2 text--secondary">
                            No skills listed yet
                        </div>
                    </div>

                    <!-- Accomplishments Section -->
                    <div class="mb-6">
                        <h3 class="text-h6 font-weight-bold mb-4">
                            <v-icon color="primary" class="mr-2">mdi-trophy</v-icon>
                            Accomplishments
                        </h3>

                        <div v-if="user.accomplishments && user.accomplishments.length > 0">
                            <v-chip v-for="(accomplishment, index) in user.accomplishments" :key="index"
                                class="mr-2 mb-2" color="green" outlined small>
                                {{ accomplishment }}
                            </v-chip>
                        </div>
                        <div v-else class="text-body-2 text--secondary">
                            No accomplishments listed yet
                        </div>
                    </div>

                    <!-- Personal Statistics Section -->
                    <div>
                        <h3 class="text-h6 font-weight-bold mb-4">
                            <v-icon color="primary" class="mr-2">mdi-chart-box</v-icon>
                            My Statistics
                        </h3>

                        <v-row class="mb-4">
                            <v-col cols="6" md="3">
                                <v-card elevation="0" outlined class="pa-3 text-center stats-card">
                                    <v-icon color="primary" class="mb-2">mdi-format-list-bulleted</v-icon>
                                    <div class="text-h6 font-weight-bold">{{ userStats.total_tasks }}</div>
                                    <div class="text-caption text--secondary">Total Tasks</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card elevation="0" outlined class="pa-3 text-center stats-card">
                                    <v-icon color="orange" class="mb-2">mdi-progress-clock</v-icon>
                                    <div class="text-h6 font-weight-bold">{{ userStats.in_progress_tasks }}</div>
                                    <div class="text-caption text--secondary">In Progress</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card elevation="0" outlined class="pa-3 text-center stats-card">
                                    <v-icon color="success" class="mb-2">mdi-check-circle</v-icon>
                                    <div class="text-h6 font-weight-bold">{{ userStats.completed_tasks }}</div>
                                    <div class="text-caption text--secondary">Completed</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card elevation="0" outlined class="pa-3 text-center stats-card">
                                    <v-icon color="error" class="mb-2">mdi-alert-circle</v-icon>
                                    <div class="text-h6 font-weight-bold">{{ userStats.overdue_tasks }}</div>
                                    <div class="text-caption text--secondary">Overdue</div>
                                </v-card>
                            </v-col>
                        </v-row>
                    </div>
                </v-card-text>
            </v-card>

            <!-- Quick Overview Section -->
            <v-divider class="mb-6" />

            <v-card elevation="1">
                <v-card-title>
                    <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
                    <span style="font-size: 1.2rem; font-weight: 500;">Quick Overview</span>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col v-for="(cardInfo, cardInfoIndex) in overview" :key="cardInfoIndex" md="4" sm="6" xs="12">
                            <v-card elevation="0" class="overview-card">
                                <v-card-title>
                                    <v-avatar color="primary" size="40">
                                        <v-icon dark size="20" color="white">
                                            {{ cardInfo.icon }}
                                        </v-icon>
                                    </v-avatar>
                                    <v-spacer />
                                    <v-chip small outlined color="primary">
                                        <span style="font-size: 0.8rem; font-weight: 500;">{{ cardInfo.chip
                                        }}</span>
                                    </v-chip>
                                </v-card-title>
                                <v-card-subtitle style="position: relative; top: 4px;">
                                    <span style="font-weight: 600; font-size: 0.9rem;">{{ cardInfo.title }}</span>
                                </v-card-subtitle>
                                <v-card-title style="position: relative; bottom: 15px;">
                                    <span style="font-weight: 700; font-size: 1.60rem;">{{ cardInfo.number }}</span>
                                </v-card-title>
                                <v-card-subtitle style="position: relative; bottom: 10px;">
                                    <span style="font-weight: 600; font-size: 0.8rem;">{{ cardInfo.last_review
                                    }}</span>
                                </v-card-subtitle>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </div>

        <!-- Profile Edit Dialog -->
        <ProfileEditDialog v-model="profileEditDialog" :user="user" @profile-updated="onProfileUpdated" />

        <!-- Change Password Dialog -->
        <ChangePasswordDialog v-model="showChangePassword" />
    </v-container>
</template>

<script>
/**vuex */
import { mapGetters } from "vuex"
import ProfileEditDialog from "@/components/modules/ProfileEditDialog.vue"
import ChangePasswordDialog from "@/components/modules/ChangePasswordDialog.vue"

export default {
    components: {
        ProfileEditDialog,
        ChangePasswordDialog,
    },
    data() {
        return {
            profileEditDialog: false,
            showChangePassword: false,
            overview: [
                {
                    "icon": "mdi-chart-bar",
                    "chip": "On Track",
                    "title": "Current Performance",
                    "number": "4.2 / 5.0",
                    "last_review": "8% increase from last review"
                },
                {
                    "icon": "mdi-seal",
                    "chip": "Next Level",
                    "title": "Career Progress",
                    "number": "67%",
                    "last_review": "Marketing Team Lead"
                },
                {
                    "icon": "mdi-seal",
                    "chip": "Plan Ahead",
                    "title": "Available Time Off",
                    "number": "12 Days",
                    "last_review": "Leave: 12"
                }
            ]
        };
    },
    mounted() {
        document.title = "DG Tasks - My Profile"
    },
    computed: {
        ...mapGetters({
            user: 'auth/get_user',
            userStats: 'auth/get_user_stats',
        }),
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        }
    },
    methods: {
        getUserFullName(user) {
            if (user.first_name || user.last_name) {
                return `${user.first_name || ""} ${user.last_name || ""}`.trim();
            }
            return user.name || "No Name";
        },
        getUserInitials(user) {
            if (user.first_name && user.last_name) {
                return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
            }
            if (user.name) {
                const parts = user.name.split(" ");
                if (parts.length >= 2) {
                    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                }
                return user.name[0].toUpperCase();
            }
            return "NN";
        },
        getProfilePictureUrl(profilePicture) {
            if (!profilePicture) return null;

            // If it's already a full URL, return as is
            if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
                return profilePicture;
            }

            // Convert old /media/ format to new /storage/ format
            let picturePath = profilePicture;
            if (picturePath.startsWith('/media/')) {
                picturePath = picturePath.replace('/media/', '/storage/');
            }

            // Otherwise, construct the full URL with the API base URL
            const apiUrl = this.apiUrl.endsWith('/') ? this.apiUrl.slice(0, -1) : this.apiUrl;
            picturePath = picturePath.startsWith('/') ? picturePath : `/${picturePath}`;
            return `${apiUrl}${picturePath}`;
        },
        formatDate(date) {
            if (!date) return "N/A";
            return this.$date(date).format("MMMM DD, YYYY");
        },
        openEditProfile() {
            this.profileEditDialog = true;
        },
        async onProfileUpdated() {
            // Refresh user data to show updated information
            await this.getUser(this.user.id);
            this.$toast.success('Profile information updated!');
        },
        // User service methods
        async getUser(id) {
            try {
                const response = await this.$axios.get(`/api/users/${id}`)
                const { data } = response
                this.$store.commit('auth/set_user', data)
            } catch (error) {
                console.log('error on fetching user information - service layer', error);
            }
        }
    },
};
</script>

<style scoped lang="scss">
.profile-header {
    padding: 24px 0;
}

.info-item {
    border-radius: 8px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #f5f5f5;
    }
}

.overview-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
}

.stats-card {
    border-radius: 12px;
    transition: all 0.2s ease;
    min-height: 100px;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }
}

.v-card {
    border-radius: 12px;
}

.v-chip {
    font-weight: 500;
}

.text-h6 {
    color: #424242;
}
</style>